import * as THREE from 'three';

export interface ScreenConfig {
    id: string;
    label: string;
    sublabel?: string;
    videoUrl?: string;
    color: THREE.Color;
    route?: string;
}

export class TVScreen {
    public mesh: THREE.Group;
    public screenMesh: THREE.Mesh;
    public config: ScreenConfig;
    public isHovered: boolean = false;

    private frameMaterial!: THREE.MeshStandardMaterial;
    private screenMaterial!: THREE.MeshBasicMaterial; // Changed to Basic for guaranteed visibility
    private glowMaterial!: THREE.MeshBasicMaterial;
    private emissiveIntensity: number = 0.5;
    private targetEmissive: number = 0.5;

    // Screen dimensions
    private readonly screenWidth = 4.0;
    private readonly screenHeight = 2.25;
    private readonly frameDepth = 0.12;
    private readonly bezelWidth = 0.06;

    constructor(config: ScreenConfig) {
        this.config = config;
        this.mesh = new THREE.Group();
        this.mesh.name = `screen_${config.id}`;
        this.mesh.userData.screenId = config.id;
        this.mesh.userData.route = config.route;

        this.createScreen();
        this.screenMesh = this.mesh.children.find(c => c.name === 'screen_surface') as THREE.Mesh;

        // Initialize dynamic canvas texture
        this.createSciFiInterface();
    }

    private createScreen(): void {
        // Create outer frame
        const frameGroup = new THREE.Group();
        frameGroup.name = 'frame_group';

        const frameGeometry = new THREE.BoxGeometry(
            this.screenWidth + this.bezelWidth * 2 + 0.1,
            this.screenHeight + this.bezelWidth * 2 + 0.1,
            this.frameDepth
        );
        this.frameMaterial = new THREE.MeshStandardMaterial({
            color: 0x0a0a12,
            metalness: 0.95,
            roughness: 0.05,
        });
        const frame = new THREE.Mesh(frameGeometry, this.frameMaterial);
        frame.castShadow = true;
        frame.name = 'frame';
        frameGroup.add(frame);

        // Inner frame border (accent)
        const innerFrameGeo = new THREE.BoxGeometry(
            this.screenWidth + this.bezelWidth * 2,
            this.screenHeight + this.bezelWidth * 2,
            this.frameDepth + 0.01
        );
        const innerFrameMat = new THREE.MeshStandardMaterial({
            color: 0x15151f,
            metalness: 0.9,
            roughness: 0.1,
        });
        const innerFrame = new THREE.Mesh(innerFrameGeo, innerFrameMat);
        innerFrame.position.z = 0.01;
        frameGroup.add(innerFrame);

        this.mesh.add(frameGroup);

        // Screen surface
        const screenGeometry = new THREE.PlaneGeometry(this.screenWidth, this.screenHeight);

        // Revert to Sci-Fi Material but with Basic Material for safety
        this.screenMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            side: THREE.DoubleSide
        });

        const screenSurface = new THREE.Mesh(screenGeometry, this.screenMaterial);

        // FORCE Z-OFFSET: Move it WAY out to guarantee it's not inside the frame
        screenSurface.position.z = this.frameDepth / 2 + 0.05;

        screenSurface.name = 'screen_surface';
        screenSurface.userData.screenId = this.config.id;
        screenSurface.userData.route = this.config.route;
        screenSurface.userData.isScreen = true;
        this.mesh.add(screenSurface);

        // Glow
        const glowGeometry = new THREE.PlaneGeometry(
            this.screenWidth + 0.3,
            this.screenHeight + 0.3
        );
        this.glowMaterial = new THREE.MeshBasicMaterial({
            color: this.config.color,
            transparent: true,
            opacity: 0.15,
            side: THREE.DoubleSide,
        });
        const glow = new THREE.Mesh(glowGeometry, this.glowMaterial);
        glow.position.z = this.frameDepth / 2 - 0.01;
        glow.name = 'glow';
        this.mesh.add(glow);

        this.createEdgeLights();
        this.createStatusBar();
    }

    private createEdgeLights(): void {
        const edgeColor = this.config.color;
        const edgeMaterial = new THREE.MeshBasicMaterial({
            color: edgeColor,
            transparent: true,
            opacity: 0.7,
        });
        const topEdgeGeo = new THREE.BoxGeometry(this.screenWidth - 0.5, 0.015, 0.02);
        const topEdge = new THREE.Mesh(topEdgeGeo, edgeMaterial);
        topEdge.position.set(0, this.screenHeight / 2 + this.bezelWidth + 0.02, this.frameDepth / 2 + 0.01);
        topEdge.name = 'top_edge';
        this.mesh.add(topEdge);

        const bottomEdge = topEdge.clone();
        bottomEdge.position.y = -this.screenHeight / 2 - this.bezelWidth - 0.02;
        bottomEdge.name = 'bottom_edge';
        this.mesh.add(bottomEdge);

        // Corner accents (removed from original, but kept in the provided snippet, so re-adding)
        const cornerGeo = new THREE.BoxGeometry(0.08, 0.08, 0.02);
        const corners = [
            [-this.screenWidth / 2 - this.bezelWidth, this.screenHeight / 2 + this.bezelWidth],
            [this.screenWidth / 2 + this.bezelWidth, this.screenHeight / 2 + this.bezelWidth],
            [-this.screenWidth / 2 - this.bezelWidth, -this.screenHeight / 2 - this.bezelWidth],
            [this.screenWidth / 2 + this.bezelWidth, -this.screenHeight / 2 - this.bezelWidth],
        ];

        corners.forEach(([x, y], i) => {
            const corner = new THREE.Mesh(cornerGeo, edgeMaterial.clone());
            corner.position.set(x, y, this.frameDepth / 2 + 0.01);
            corner.name = `corner_${i}`;
            this.mesh.add(corner);
        });
    }

    private createStatusBar(): void {
        const barWidth = this.screenWidth * 0.3;
        const barGeo = new THREE.BoxGeometry(barWidth, 0.02, 0.015);
        const barMat = new THREE.MeshBasicMaterial({
            color: 0x00ff88,
            transparent: true,
            opacity: 0.8,
        });
        const bar = new THREE.Mesh(barGeo, barMat);
        bar.position.set(0, -this.screenHeight / 2 - this.bezelWidth - 0.06, this.frameDepth / 2);
        bar.name = 'status_bar';
        this.mesh.add(bar);

        // Decorative dots (removed from original, but kept in the provided snippet, so re-adding)
        const dotGeo = new THREE.CircleGeometry(0.02, 16);
        const dotMat = new THREE.MeshBasicMaterial({
            color: this.config.color,
            transparent: true,
            opacity: 0.6,
        });

        for (let i = 0; i < 3; i++) {
            const dot = new THREE.Mesh(dotGeo, dotMat);
            dot.position.set(
                barWidth / 2 + 0.08 + i * 0.06,
                -this.screenHeight / 2 - this.bezelWidth - 0.06,
                this.frameDepth / 2 + 0.01
            );
            this.mesh.add(dot);
        }
    }

    private createSciFiInterface(): void {
        const width = 1024;
        const height = 576;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { alpha: false })!;

        // Initial bright fill to confirm canvas is working
        ctx.fillStyle = '#1a1a2e';
        ctx.fillRect(0, 0, width, height);
        ctx.font = '40px Arial';
        ctx.fillStyle = 'white';
        ctx.textAlign = 'center';
        ctx.fillText("INITIALIZING SYSTEM...", width / 2, height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        texture.anisotropy = 16;
        texture.colorSpace = THREE.SRGBColorSpace;

        // Ensure the texture is assigned - BasicMaterial only needs map
        this.screenMaterial.map = texture;
        this.screenMaterial.needsUpdate = true;

        const baseColor = this.config.color.getStyle();

        let time = 0;

        const animate = () => {
            time += 0.01;

            // 1. Clear background (Dark Hex Grid)
            ctx.fillStyle = '#05050a'; // Deep dark void
            ctx.fillRect(0, 0, width, height);

            // 2. Dynamic Gradient Background
            const gradient = ctx.createLinearGradient(0, 0, width, height);
            gradient.addColorStop(0, '#0a0a12');
            gradient.addColorStop(1, '#000000');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, width, height);

            // 3. Draw Hex Grid
            ctx.strokeStyle = this.fadeColor(baseColor, 0.15);
            ctx.lineWidth = 1;
            const hexSize = 60;
            const yOffset = (time * 20) % (hexSize * 1.732);

            for (let y = -hexSize; y < height + hexSize; y += hexSize * 0.866) {
                for (let x = 0; x < width + hexSize; x += hexSize * 1.5) {
                    const isEven = Math.floor((y - yOffset) / (hexSize * 0.866)) % 2 === 0;
                    const xPos = x + (isEven ? hexSize * 0.75 : 0);
                    const yPos = y + yOffset;

                    this.drawHexagon(ctx, xPos, yPos, hexSize * 0.5);
                }
            }

            // 4. Scanning lines (Holographic feel)
            const scanY = (time * 100) % height;
            const scanHeight = 50;
            const scanGrad = ctx.createLinearGradient(0, scanY, 0, scanY + scanHeight);
            scanGrad.addColorStop(0, 'rgba(0,0,0,0)');
            scanGrad.addColorStop(0.5, this.fadeColor(baseColor, 0.3));
            scanGrad.addColorStop(1, 'rgba(0,0,0,0)');

            ctx.fillStyle = scanGrad;
            ctx.fillRect(0, scanY, width, scanHeight);

            // 5. Data Stream / Code Lines (Abstract)
            ctx.fillStyle = this.fadeColor(baseColor, 0.5);
            for (let i = 0; i < 10; i++) {
                const x = Math.random() * width;
                const y = Math.random() * height;
                const w = Math.random() * 100 + 20;
                if (Math.random() > 0.95) { // Flicker effect
                    ctx.fillRect(x, y, w, 2);
                }
            }

            // 6. Central HUD Element
            ctx.strokeStyle = this.fadeColor(baseColor, 0.8);
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(width / 2, height / 2, 100 + Math.sin(time * 2) * 10, 0, Math.PI * 2);
            ctx.stroke();

            ctx.strokeStyle = this.fadeColor(baseColor, 0.4);
            ctx.beginPath();
            ctx.arc(width / 2, height / 2, 120, time, time + Math.PI);
            ctx.stroke();

            // 7. Text Display
            ctx.font = 'bold 64px "Orbitron", sans-serif';
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'center';
            ctx.shadowColor = baseColor;
            ctx.shadowBlur = 15;
            ctx.fillText(this.config.label, width / 2, height / 2 + 20);
            ctx.shadowBlur = 0;

            if (this.config.sublabel) {
                ctx.font = '32px "Inter", sans-serif';
                ctx.fillStyle = '#aaaaaa';
                ctx.fillText(this.config.sublabel, width / 2, height / 2 + 80);
            }

            texture.needsUpdate = true;
            requestAnimationFrame(animate);
        };

        animate();
    }

    private drawHexagon(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
            const angle = 2 * Math.PI / 6 * i;
            const xPos = x + r * Math.cos(angle);
            const yPos = y + r * Math.sin(angle);
            if (i === 0) ctx.moveTo(xPos, yPos);
            else ctx.lineTo(xPos, yPos);
        }
        ctx.closePath();
        ctx.stroke();
    }

    private fadeColor(colorStyle: string, alpha: number): string {
        if (colorStyle.startsWith('rgb')) {
            return colorStyle.replace(')', `, ${alpha})`).replace('rgb', 'rgba');
        }
        return colorStyle;
    }

    public setHovered(hovered: boolean): void {
        this.isHovered = hovered;
        this.targetEmissive = hovered ? 1.0 : 0.5;
    }

    public update(deltaTime: number): void {
        // Smooth intensity transition for BasicMaterial (using color tint as proxy for intensity)
        this.emissiveIntensity += (this.targetEmissive - this.emissiveIntensity) * deltaTime * 6;

        // For MeshBasicMaterial, we can't use emissiveIntensity.
        // Instead, we ensure the map is always full brightness (white color).
        // The "glow" effect comes from the separate glow mesh.
        this.screenMaterial.color.setScalar(this.emissiveIntensity); // Modulate brightness

        this.glowMaterial.opacity = this.isHovered ? 0.35 : 0.15;

        // Update edge lights
        this.mesh.traverse((child) => {
            if (child.name === 'top_edge' || child.name === 'bottom_edge' || child.name.startsWith('corner_')) {
                const mat = (child as THREE.Mesh).material as THREE.MeshBasicMaterial;
                mat.opacity = this.isHovered ? 1.0 : 0.7;
            }
        });
    }

    public dispose(): void {
        this.frameMaterial.dispose();
        this.screenMaterial.dispose();
        this.glowMaterial.dispose();
        this.mesh.traverse((obj) => {
            if (obj instanceof THREE.Mesh) obj.geometry.dispose();
        });
    }
}
